/* eslint-disable no-prototype-builtins */
import { cloneDeep, isArray, isEmpty, uniq } from 'lodash';
import { dispatch, store } from 'src/redux/store';
import { handleTablePreferences, handleTablePreferencesEdit } from 'src/redux/slices/tablePreferences';

export const getCurrentTableData = (results, tableTitle) => results?.filter((ele) => ele.table_name === tableTitle) || [];

// Applies an explicit column ORDER to the live grid via apiRef — extracted
// from the inline logic in handleTablePreference so handleToggleCombineName/
// handleToggleCombineContact (useColumnVisibilityChange.js) can apply the
// same immediate reorder right after a toggle, without waiting for the
// save-preference round-trip to come back through Redux and re-trigger
// handleTablePreference. Skips pinned-right columns — their position is
// managed by setPinnedColumns.
export const applyColumnOrder = (apiRef, orderedFields) => {
    if (!apiRef?.current?.setColumnIndex || !Array.isArray(orderedFields) || orderedFields.length === 0) return;
    const PINNED_RIGHT = ['contact', 'viewDetails', 'actions'];
    const reorderableCols = orderedFields.filter((f) => !PINNED_RIGHT.includes(f));
    const applyOrder = () => {
        try {
            const internalOffset = apiRef.current.getColumn?.('__check__') ? 1 : 0;
            reorderableCols.forEach((field, targetIdx) => {
                if (apiRef.current.getColumn?.(field)) {
                    apiRef.current.setColumnIndex(field, targetIdx + internalOffset);
                }
            });
        } catch (e) {
            // DataGrid not ready — ignore
        }
    };
    requestAnimationFrame(() => { setTimeout(applyOrder, 200); });
};

// "Combine name"/"Combine contact" both collapse N split fields into one
// combined field, keeping ORDER stable by substituting in place at the first
// split field's index rather than letting the combined field get appended at
// the end (MUI DataGridPro appends a column it has never seen before to the
// end of the live column order — this is what happens if the combined field
// is simply missing from the saved `columns` list).
//
//   toCombined=true:  [a, email, phone, b] -> [a, contact_details, b]
//   toCombined=false: [a, contact_details, b] -> [a, email, phone, b]
export const swapColumnsInPlace = (columns, { combinedField, splitFields }, toCombined) => {
    const list = Array.isArray(columns) ? [...columns] : [];
    if (toCombined) {
        const anchorIdx = list.indexOf(splitFields[0]);
        if (anchorIdx !== -1) {
            list.splice(anchorIdx, 1, combinedField);
        } else if (!list.includes(combinedField)) {
            list.push(combinedField);
        }
        splitFields.slice(1).forEach((field) => {
            const idx = list.indexOf(field);
            if (idx !== -1) list.splice(idx, 1);
        });
        return list;
    }
    const combinedIdx = list.indexOf(combinedField);
    if (combinedIdx !== -1) {
        list.splice(combinedIdx, 1, ...splitFields);
    } else {
        splitFields.forEach((field) => {
            if (!list.includes(field)) list.push(field);
        });
    }
    return list;
};

// Gives a newly-added, always-visible column (e.g. next_task_inline,
// contact_details when its usual `email` anchor is also missing) a sensible
// DEFAULT position — right after `afterField` (or `fallbackAfterField` if
// that's absent too, or the end if neither exists) — in a saved `columns`
// order list that predates the field, instead of leaving it to append at
// wherever MUI DataGridPro puts a column it has never seen in the live order
// before. Display-only (derived on load, never persisted) — a user who later
// drags the column still has their choice saved normally.
export const insertColumnAfterAnchor = (columns, field, afterField, fallbackAfterField) => {
    const list = Array.isArray(columns) ? [...columns] : [];
    if (list.includes(field)) return list;
    const primaryIdx = list.indexOf(afterField);
    if (primaryIdx !== -1) {
        list.splice(primaryIdx + 1, 0, field);
        return list;
    }
    const fallbackIdx = fallbackAfterField ? list.indexOf(fallbackAfterField) : -1;
    if (fallbackIdx !== -1) {
        list.splice(fallbackIdx + 1, 0, field);
        return list;
    }
    list.push(field);
    return list;
};

// MUI DataGrid internal fields that should never be stored in or affected by column preferences
const MUI_INTERNAL_FIELDS = ['__check__', '__reorder__', '__detail_panel_toggle__'];

// Legacy admin-key → DataGrid-field translations. Saved BE prefs may carry the
// old keys (e.g. 'treatment_price') from before the field rename. Translate on
// load so toggles continue to work without a one-shot data migration.
// Reported by Raj 2026-05-25 — admin column picker / DataGrid field mismatches.
const LEGACY_COLUMN_KEY_MAP = {
    treatment_price: 'price',
    // 2026-05-25 — DataGrid column fields dropped the underscore prefix
    // (AttendedDateCell, ConsultationDateCell). Translate legacy saved
    // keys so existing user prefs don't silently lose the column.
    _date_attended: 'date_attended',
    _consultation_date: 'consultation_date',
};

export const handleColumnVisibility = ({ setColumnVisibilityModel, filteredData }) => {
    const columnVisibilityData = {};
    filteredData.forEach(key => {
        columnVisibilityData[key] = false;
    });
    setColumnVisibilityModel(columnVisibilityData);
};

// Validates and auto-fixes saved preferences before applying them.
// This prevents corrupted or stale preferences from breaking the UI after code updates.
const sanitizePreferences = (pref) => {
    if (!pref?.preferences) return pref;
    // Deep-clone to avoid mutating frozen Redux Immer state.
    // Shallow spread only copies top-level keys — nested objects like
    // prefs.preferences[col] remain frozen references from the store.
    const prefs = cloneDeep(pref.preferences);

    // 1. Remove MUI internal fields from columns array + translate legacy keys
    if (prefs.columns) {
        prefs.columns = prefs.columns
            .filter(c => !MUI_INTERNAL_FIELDS.includes(c))
            .map(c => LEGACY_COLUMN_KEY_MAP[c] || c);
    }
    // Mirror translation into the per-column preferences map (width/pin/etc.)
    if (prefs.preferences && typeof prefs.preferences === 'object') {
        Object.keys(LEGACY_COLUMN_KEY_MAP).forEach((oldKey) => {
            if (prefs.preferences[oldKey] && !prefs.preferences[LEGACY_COLUMN_KEY_MAP[oldKey]]) {
                prefs.preferences[LEGACY_COLUMN_KEY_MAP[oldKey]] = prefs.preferences[oldKey];
                delete prefs.preferences[oldKey];
            }
        });
    }

    // 2. Only discard when columns is present but not an array (malformed).
    // An empty array [] is a valid intentional state (user hid all columns) — do NOT return null.
    if (prefs.columns !== undefined && !Array.isArray(prefs.columns)) {
        return null;
    }

    // 3. Ensure action columns have correct pin state
    // Safe to mutate directly — prefs is a deep clone (line 25).
    // CREATE the preferences sub-object if it doesn't exist — saved data from
    // the super admin TableDefaults page often has { columns: [...] } without
    // a preferences key, so action columns lose their pin state on load.
    const ACTION_COLS = ['contact', 'viewDetails', 'actions'];
    if (!prefs.preferences) prefs.preferences = {};
    ACTION_COLS.forEach(col => {
        if (!prefs.preferences[col]) prefs.preferences[col] = {};
        prefs.preferences[col].isRightPinned = true;
        prefs.preferences[col].isLeftPinned = false;
    });

    return { ...pref, preferences: prefs };
};

export const handleTablePreference = ({ allColumns, setColumnVisibilityModel, tableTitle, setPinnedColumns, handleSorting, setIsCombinedName, setIsCombinedContact, alwaysVisibleFields, userId, setIsEnforcedByPractice, apiRef, customTabOverride }) => {
    const { tablePreferences: { results }, globalTablePreferences, practiceTablePreferences } = store.getState().tablePreferences;
    // Sanitize all preference sources before using them
    const rawUserData = getCurrentTableData(results, tableTitle);
    const rawPracticeData = getCurrentTableData(practiceTablePreferences?.results || [], tableTitle);
    const currentGlobalTableData = getCurrentTableData(globalTablePreferences?.results || [], tableTitle);

    // Sanitize ALL sources: remove MUI internals, fix empty columns, fix pin order.
    // Global was previously not sanitized — action columns lost their pin state
    // when users fell through to the global fallback.
    const userTableData = rawUserData?.map(sanitizePreferences).filter(Boolean) || [];
    const practiceTableData = rawPracticeData?.map(sanitizePreferences).filter(Boolean) || [];
    const globalTableData = currentGlobalTableData?.map(sanitizePreferences).filter(Boolean) || [];

    // Custom-tab column override — when a user is on a custom sub-tab whose
    // admin set explicit column_overrides, those win over every other source.
    // The override JSON has the same shape as a saved preference's `preferences`
    // object (columns array + per-field config), so we wrap it identically.
    // Sanitization runs to apply MUI-internal scrubbing + action-pin defaults.
    const sanitizedCustomTab = customTabOverride
        ? sanitizePreferences({ preferences: customTabOverride })
        : null;
    const customTabTableData = sanitizedCustomTab ? [sanitizedCustomTab] : [];

    // Check practice-level enforcement
    const practicePref = practiceTableData?.[0];
    const isEnforced = practicePref?.is_enforced === true;
    const targetUsers = practicePref?.target_users || [];
    const isTargeted = targetUsers.length === 0 || targetUsers.some(tu => tu === userId || tu?.id === userId);
    const enforceForUser = isEnforced && isTargeted && !isEmpty(practicePref?.preferences?.columns);

    if (setIsEnforcedByPractice) {
        setIsEnforcedByPractice(enforceForUser);
    }

    // Resolve: custom-tab override -> enforced practice -> user -> practice fallback -> global.
    // Custom-tab override sits at the top because the user explicitly chose
    // this sub-tab and the admin curated its layout for that scope.
    // Use Array.isArray instead of !isEmpty — an empty columns array [] is valid intent.
    let currentTableData;
    const customTabHasColumns = customTabTableData.length > 0 && Array.isArray(customTabTableData[0]?.preferences?.columns);
    const practiceHasColumns = isArray(practiceTableData) && practiceTableData.length > 0 && Array.isArray(practiceTableData[0]?.preferences?.columns);
    if (customTabHasColumns) {
        currentTableData = customTabTableData;
    } else if (enforceForUser) {
        currentTableData = practiceTableData;
    } else if (isArray(userTableData) && userTableData.length > 0) {
        currentTableData = userTableData;
    } else if (practiceHasColumns) {
        currentTableData = practiceTableData;
    } else {
        currentTableData = globalTableData;
    }
    if (Array.isArray(currentTableData?.[0]?.preferences?.columns)) {
        const savedColumns = currentTableData[0].preferences.columns;
        const unVisibleColumns = allColumns?.filter((el) => !savedColumns?.includes(el)) || [];
        const UpdatedVisibleColumns = allColumns?.reduce((total, currentVal) => {
            if (MUI_INTERNAL_FIELDS.includes(currentVal)) return total;
            const isNewDynamic = alwaysVisibleFields?.includes(currentVal) && !savedColumns?.includes(currentVal);
            // task_checklist_* columns arrive async, after prefs load. Omit them
            // from the model so they default to visible; the useEffect in
            // NewLeadDataGrid merges them in explicitly when they land.
            if (currentVal.startsWith('task_checklist_') && !savedColumns?.includes(currentVal)) return total;
            // Archived Leads gained the Enquiry-date column (created_at) on
            // 2026-07-09 — every saved layout predates it, so absence from
            // savedColumns means "column didn't exist yet", not "user hid it".
            // Same omit-pattern as task_checklist_* (defaults to visible).
            // Scoped to this table only: on other tables created_at absence IS
            // a deliberate hide and must keep being respected.
            if (tableTitle === 'Archived Leads' && currentVal === 'created_at' && !savedColumns?.includes(currentVal)) return total;
            total = { ...total, [currentVal]: !unVisibleColumns.includes(currentVal) || isNewDynamic };
            return total;
        }, {});
        setColumnVisibilityModel({ ...UpdatedVisibleColumns, __check__: true, __detail_panel_toggle__: false });

        // Apply saved column ORDER using apiRef. `next_task_inline` is forced
        // visible via the alwaysVisibleFields carve-out (NewLeadDataGrid.jsx)
        // but a saved `columns` list from before it existed has no entry for
        // it — give it a default position right after `lastContact`
        // (fallback: after `lead_name`) instead of letting it land wherever
        // DataGridPro appends a column it's never seen in the live order
        // before. Display-only — not persisted here.
        let orderedColumnsForDisplay = savedColumns;
        if (alwaysVisibleFields?.includes('next_task_inline') && !savedColumns.includes('next_task_inline')) {
            orderedColumnsForDisplay = insertColumnAfterAnchor(orderedColumnsForDisplay, 'next_task_inline', 'lastContact', 'lead_name');
        }
        applyColumnOrder(apiRef, orderedColumnsForDisplay);
    }
    const currentTablePreferences = currentTableData?.[0]?.preferences?.preferences || {};
    const pinnedData = { left: [], right: [] };
    Object.entries(currentTablePreferences).forEach(([key, value]) => {
        if (value.isLeftPinned) {
            pinnedData.left.push(key);
        }
        if (value.isRightPinned) {
            pinnedData.right.push(key);
        }
        if (currentTablePreferences[key]?.hasOwnProperty('sortDirection') && allColumns?.includes(key)) {
            handleSorting({ key, order: currentTablePreferences[key].sortDirection });
        }
    });
    // Normalize action column group order: Action → View Details → More (left-to-right)
    const ACTION_GROUP = ['contact', 'viewDetails', 'actions'];
    const actionCols = ACTION_GROUP.filter(col => pinnedData.right.includes(col));
    const otherCols = pinnedData.right.filter(col => !ACTION_GROUP.includes(col));
    pinnedData.right = [...otherCols, ...actionCols];
    setPinnedColumns(pinnedData);
    if (setIsCombinedName) {
        const hasSavedPref = currentTableData?.[0]?.preferences?.hasOwnProperty('isCombinedName');
        const combined = hasSavedPref ? currentTableData[0].preferences.isCombinedName : true;
        setIsCombinedName(combined);
        setColumnVisibilityModel(prev => ({
            ...prev,
            lead_name: combined,
            first_name: !combined,
            last_name: !combined,
        }));
        // Auto-migrate old saved prefs that have first_name/last_name but not lead_name
        const savedColumns = currentTableData?.[0]?.preferences?.columns || [];
        const tableId = currentTableData?.[0]?.id;
        if (!hasSavedPref && !savedColumns.includes('lead_name') && tableId && userId) {
            const insertIdx = savedColumns.indexOf('first_name');
            const migratedColumns = savedColumns.filter(c => c !== 'first_name' && c !== 'last_name');
            migratedColumns.splice(insertIdx >= 0 ? insertIdx : Math.min(2, migratedColumns.length), 0, 'lead_name');
            const currentPrefs = currentTableData[0].preferences;
            dispatch(handleTablePreferencesEdit({
                tableId,
                practiceUserId: userId,
                body: {
                    table_name: tableTitle,
                    preferences: { ...currentPrefs, columns: migratedColumns, isCombinedName: true },
                },
            }));
        }
    }
    if (setIsCombinedContact) {
        // Mirrors the isCombinedName block above, minus the migration step —
        // "Combine contact" is a new, opt-in field (default false) with no
        // legacy saved-column shape to migrate.
        const hasSavedContactPref = currentTableData?.[0]?.preferences?.hasOwnProperty('isCombinedContact');
        const combinedContact = hasSavedContactPref ? currentTableData[0].preferences.isCombinedContact : false;
        setIsCombinedContact(combinedContact);
        setColumnVisibilityModel(prev => ({
            ...prev,
            contact_details: combinedContact,
            email: !combinedContact,
            phone: !combinedContact,
        }));
        // Defensive, no PATCH: a saved record can have isCombinedContact: true
        // with a `columns` list that predates this feature (e.g. a stale
        // record written before handleToggleCombineContact started rewriting
        // `columns`, or a hand-edited global/practice default). Without this,
        // the main reorder block above already ran using the UN-swapped
        // savedColumns and would have left contact_details wherever MUI's
        // DataGridPro appends a column it has never seen in the live column
        // order before (the end) — reapply ordering so it lands at email's
        // old position instead; if `email` is ALSO absent from the saved
        // order (no anchor to swap against), fall back to right after
        // `lead_name` rather than the end.
        const savedContactColumns = currentTableData?.[0]?.preferences?.columns;
        if (combinedContact && Array.isArray(savedContactColumns) && !savedContactColumns.includes('contact_details')) {
            const reorderedContactColumns = savedContactColumns.includes('email')
                ? swapColumnsInPlace(savedContactColumns, { combinedField: 'contact_details', splitFields: ['email', 'phone'] }, true)
                : insertColumnAfterAnchor(savedContactColumns, 'contact_details', 'lead_name');
            applyColumnOrder(apiRef, reorderedContactColumns);
        }
    }
};

export const applyTablePreferences = ({ columns, tableName, isMultiTreatmentTable }) => {
    const { tablePreferences: { results }, globalTablePreferences, practiceTablePreferences } = store.getState().tablePreferences;
    const tableData = getCurrentTableData(results, tableName);
    const practiceTableData = getCurrentTableData(practiceTablePreferences?.results || [], tableName);
    const currentGlobalTableData = getCurrentTableData(globalTablePreferences?.results || [], tableName);
    // Cascade: user → practice → global
    let currentTableData = currentGlobalTableData;
    if (isArray(tableData) && tableData.length > 0) {
        currentTableData = tableData;
    } else if (isArray(practiceTableData) && practiceTableData.length > 0) {
        currentTableData = practiceTableData;
    }
    const modifiedColumns = columns?.map((ele) => {
        const currentColumn = currentTableData?.[0]?.preferences?.preferences?.[ele.field];
        return { ...ele, width: currentColumn?.width || ele.width };
    });
    if (isMultiTreatmentTable) return columns;
    return modifiedColumns;
};

export const handleColumnWidthChange = ({ data, userId, tableName, details }) => {
    const { tablePreferences: { results }, globalTablePreferences, practiceTablePreferences } = store.getState().tablePreferences;
    const tableData = getCurrentTableData(results, tableName);
    const practiceTableData = getCurrentTableData(practiceTablePreferences?.results || [], tableName);
    const currentGlobalTableData = getCurrentTableData(globalTablePreferences?.results || [], tableName);
    let currentTableData = currentGlobalTableData;
    if (isArray(tableData) && tableData.length > 0) { currentTableData = tableData; }
    else if (isArray(practiceTableData) && practiceTableData.length > 0) { currentTableData = practiceTableData; }
    const currentTablePreferences = currentTableData?.[0]?.preferences || {};
    const currentTableFieldPreferences = currentTablePreferences?.preferences?.[data?.colDef?.field] || {};
    const preferences = currentTablePreferences?.preferences || {};
    const payload = {
        body: {
            table_name: tableName,
            preferences: { ...currentTablePreferences, preferences: { ...preferences, [data?.colDef?.field]: { ...currentTableFieldPreferences, width: data?.width } } }
        },
        practiceUserId: userId,
    };
    if (isArray(tableData) && tableData.length > 0) {
        payload.tableId = currentTableData[0].id;
        dispatch(handleTablePreferencesEdit(payload));
    } else {
        payload.body.preferences.columns = details?.api?.getVisibleColumns()?.map((ele) => ele?.field)?.filter((f) => !MUI_INTERNAL_FIELDS.includes(f));
        dispatch(handleTablePreferences(payload));
    }
};
export const handleSortingChange = ({ userId, tableName, details, key, order }) => {
    const { tablePreferences: { results }, globalTablePreferences, practiceTablePreferences } = store.getState().tablePreferences;
    const tableData = getCurrentTableData(results, tableName);
    const practiceTableData = getCurrentTableData(practiceTablePreferences?.results || [], tableName);
    const currentGlobalTableData = getCurrentTableData(globalTablePreferences?.results || [], tableName);
    let currentTableData = currentGlobalTableData;
    if (isArray(tableData) && tableData.length > 0) { currentTableData = tableData; }
    else if (isArray(practiceTableData) && practiceTableData.length > 0) { currentTableData = practiceTableData; }
    const currentTablePreferences = currentTableData?.[0]?.preferences || {};
    const currentTableFieldPreferences = currentTablePreferences?.preferences?.[key] || {};
    const preferences = currentTablePreferences?.preferences || {};
    const preferenceCopy = cloneDeep(preferences);
    Object.keys(preferenceCopy).forEach(key => {
        if (preferenceCopy[key]?.hasOwnProperty('sortDirection')) {
            delete preferenceCopy[key].sortDirection;
        }
    });
    const payload = {
        body: {
            table_name: tableName,
            preferences: { ...currentTablePreferences, preferences: { ...preferenceCopy, [key]: { ...currentTableFieldPreferences, sortDirection: order } } }
        },
        practiceUserId: userId,
    };
    if (isArray(tableData) && tableData.length > 0) {
        payload.tableId = currentTableData[0].id;
        dispatch(handleTablePreferencesEdit(payload));
    } else {
        payload.body.preferences.columns = details?.api?.getVisibleColumns()?.map((ele) => ele?.field)?.filter((f) => !MUI_INTERNAL_FIELDS.includes(f));
        dispatch(handleTablePreferences(payload));
    }
};

export const ChangePinnedColumns = ({ updatedPinnedColumns, setPinnedColumns, userId, tableName, details }) => {
    const { tablePreferences: { results }, globalTablePreferences, practiceTablePreferences } = store.getState().tablePreferences;
    // Clone to avoid mutating the caller's (MUI-owned) object.
    const pinned = {
        left: [...(updatedPinnedColumns.left ?? [])],
        right: [...(updatedPinnedColumns.right ?? [])],
    };
    // Ensure action columns are always pinned right, regardless of user action.
    // Without this, unpinning ANY column saves the full pin state — and if
    // action columns aren't in pinned.right, they lose their pin.
    const ACTION_GROUP = ['contact', 'viewDetails', 'actions'];
    ACTION_GROUP.forEach(col => {
        if (!pinned.right.includes(col)) {
            pinned.right.push(col);
        }
    });
    // Normalize order: other pinned-right columns first, then action group
    const actionCols = ACTION_GROUP.filter(col => pinned.right.includes(col));
    const otherCols = pinned.right.filter(col => !ACTION_GROUP.includes(col));
    pinned.right = [...otherCols, ...actionCols];
    setPinnedColumns(pinned);
    const tableData = getCurrentTableData(results, tableName);
    const practiceTableData = getCurrentTableData(practiceTablePreferences?.results || [], tableName);
    const currentGlobalTableData = getCurrentTableData(globalTablePreferences?.results || [], tableName);
    let currentTableData = currentGlobalTableData;
    if (isArray(tableData) && tableData.length > 0) { currentTableData = tableData; }
    else if (isArray(practiceTableData) && practiceTableData.length > 0) { currentTableData = practiceTableData; }
    const preferences = currentTableData?.[0]?.preferences || {};
    const currentTablePreferences = preferences?.preferences || {};
    const pinnedData = {};
    const totalPinnedData = pinned.left.concat(pinned.right);
    const preferenceKeys = Object.keys(currentTablePreferences)?.map((ele) => ele);
    const uniqPreferenceKeys = uniq([...preferenceKeys, ...totalPinnedData]);
    uniqPreferenceKeys.forEach((key) => {
        const oldPreferenceData = currentTablePreferences[key] || {};
        pinnedData[key] = {
            ...oldPreferenceData, isLeftPinned: pinned.left.includes(key),
            isRightPinned: pinned.right.includes(key)
        };
    });
    const payload = {
        body: {
            table_name: tableName,
            preferences: { ...preferences, preferences: { ...currentTablePreferences, ...pinnedData } }
        },
        practiceUserId: userId,
    };
    if (isArray(tableData) && tableData.length > 0) {
        payload.tableId = currentTableData[0].id;
        dispatch(handleTablePreferencesEdit(payload));
    } else {
        payload.body.preferences.columns = details?.api?.getVisibleColumns()?.map((ele) => ele?.field)?.filter((f) => !MUI_INTERNAL_FIELDS.includes(f));
        dispatch(handleTablePreferences(payload));
    }
};